/**
 * Whether a living thing is worth building a body for.
 *
 * Every creature on the map used to get one whether or not anybody could see
 * it. Ninety fish, fourteen animals, forty orcs and six villagers came to
 * seven hundred and thirty-nine draw calls a frame, and about five hundred and
 * fifty of those were for things over the horizon - most of a frame spent
 * drawing what nobody was looking at, on a machine whose graphics memory is
 * the system memory.
 *
 * **Two distances, not one.** A body is built at SIGHT and kept until FORGET.
 * With a single distance, a player standing on the line would make and destroy
 * the same pig sixty times a second - which is worse than drawing it, because
 * building a body allocates and freeing one gives the driver work.
 *
 * Pure arithmetic on two points. The renderer supplies the camera and the
 * creature; this decides, and can be argued with in a test.
 */

/** Near enough to be given a body. */
export const ENTITY_SIGHT = 56;

/**
 * Far enough to lose one.
 *
 * The gap between this and SIGHT is the hysteresis. Sixteen blocks is several
 * seconds of walking, so a body is never built and destroyed in the same
 * breath.
 */
export const ENTITY_FORGET = 72;

/**
 * @param hasBody whether this creature is already drawn, which is what makes
 * the rule sticky rather than a bare distance test.
 */
export function worthDrawing(
  ex: number, ey: number, ez: number,
  cx: number, cy: number, cz: number,
  hasBody: boolean,
  sight = ENTITY_SIGHT,
  forget = ENTITY_FORGET,
): boolean {
  const dx = ex - cx;
  const dy = ey - cy;
  const dz = ez - cz;
  const limit = hasBody ? forget : sight;
  return dx * dx + dy * dy + dz * dz <= limit * limit;
}
